import React, { useEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";

// Assets (mantieni i tuoi path)
import bgAnimation from "../assets/json/sezione-01-bkg-1920x1080.json"; // sfondo sezione
import animation1 from "../assets/json/sezione-01-fiore-01.json"; // 1° fiore
import animation2 from "../assets/json/sezione-01-fiore-02.json"; // 2° fiore
import animation3 from "../assets/json/sezione-01-fiore-03.json"; // 3° fiore
import fioriCenter from "../assets/json/sezione-01-fiori.json"; // fiori al centro dell'orbita
import icon1 from "../assets/json/sezione-01-ico-01.json"; // Vision icon
import icon2 from "../assets/json/sezione-01-ico-02.json"; // Mission icon
import icon3 from "../assets/json/sezione-01-ico-03.json"; // Purpose icon
import arrowIcon from "../assets/images/Vector_btn.png";

import ScrollDown from "../assets/json/mouse";

const slides = [
  {
    title: "Purpose",
    text: "Taking care of people for a better world, together.",
    animation: animation3,
    icon: icon3,
    color: "#BCCF00",
  },
  {
    title: "Vision",
    text: "Developing the wellbeing of people and communities, creating trust over time.",
    animation: animation2,
    icon: icon2,
    color: "#BB7CB3",
  },
  {
    title: "Mission",
    text: "Protecting and helping people with simple and sustainable solutions, inspired by our mutuality principles.",
    animation: animation1,
    icon: icon1,
    color: "#F1B700",
  },
];

// Ciclo della header: Vision "neutra" (tutti i caleidoscopi uguali) -> Purpose
// -> Mission -> Vision ingrandita -> di nuovo Vision neutra, in loop.
// idx fa riferimento all'array `slides` sopra (0=Purpose, 1=Vision, 2=Mission).
const CYCLE = [
  { idx: 1, expanded: false, hold: 20000 }, // neutro: Vision in alto, tutti piccoli
  { idx: 0, expanded: true, hold: 5000 },   // Purpose ingrandito
  { idx: 2, expanded: true, hold: 5000 },   // Mission ingrandito
  { idx: 1, expanded: true, hold: 5000 },   // Vision ingrandito
];

function useReducedMotion() {
  const [pref, setPref] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPref(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return pref;
}

const Hero = () => {
  const reduced = useReducedMotion();

  const bgLottieRef = useRef(null);

useEffect(() => {
  const inst = bgLottieRef.current;
  if (!inst) return;
  if (reduced) inst.pause();
  else inst.play();
}, [reduced]);

  // Responsiveness
  const [isMobile, setIsMobile] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const [radius, setRadius] = useState(200);

  useEffect(() => {
  const onResize = () => {
    const w = window.innerWidth;

    if (w < 768) {
      setIsMobile(true);
      setIsMedium(false);
      setRadius(60);   // mobile
    } else if (w < 1330) {
      setIsMobile(false);
      setIsMedium(true);
      setRadius(160);  // medium
    } else {
      setIsMobile(false);
      setIsMedium(false);
      setRadius(230);  // large
    }
  };

  onResize();
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);

  // Angoli base come nel tuo codice
  const baseAngles = useMemo(() => [0, 120, 240], []);
  const targetAngle = 290;

  // Stato logico (no 60fps)
  const [activeIndex, setActiveIndex] = useState(CYCLE[0].idx);
  const [expanded, setExpanded] = useState(CYCLE[0].expanded);
  const [cyclePos, setCyclePos] = useState(0);
  const [panelAnimating, setPanelAnimating] = useState(false);

  // Refs globali
  const rootRef = useRef(null);
  const orbitRef = useRef(null);
  const currentAngleRef = useRef(0);
  const activeIndexRef = useRef(CYCLE[0].idx);
  const cyclePosRef = useRef(0);
  const autoplayRef = useRef(null);
  const timersRef = useRef({ out: null, in: null });

  const speedMs = 2500; // durata rotazione (ms)
  const panelAppearDelay = 300;

  // Utils
  const norm = (deg) => ((deg % 360) + 360) % 360;
  const shortestDelta = (from, to) => {
    let d = norm(to) - norm(from);
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    return d;
  };

  // Ruota l’anello e aggiorna variabile CSS per la contro-rotazione
  const applyOrbitRotation = (deg) => {
    const node = orbitRef.current;
    if (!node) return;
    node.style.transitionProperty = "transform";
    node.style.transitionTimingFunction = "cubic-bezier(0.22, 1, 0.36, 1)";
    node.style.transitionDuration = `${speedMs}ms`;
    node.style.transform = `rotate(${deg}deg)`;
    node.style.willChange = "transform";

    // var CSS usata dentro gli item per rimanere "dritti"
    rootRef.current?.style.setProperty("--orbit-angle", `${deg}deg`);
  };

  // Passa allo step richiesto del ciclo (stile slider vanilla).
  // Ruota l'anello solo se lo slide da mostrare cambia rispetto al precedente
  // (es. l'ultimo step "Vision ingrandita" -> "Vision neutra" non ruota).
  const goToStep = (nextPos) => {
    const step = CYCLE[nextPos];
    const idxChanged = step.idx !== activeIndexRef.current;

    setPanelAnimating(true);

    window.clearTimeout(timersRef.current.out);
    timersRef.current.out = window.setTimeout(() => {
      if (idxChanged) {
        const from = currentAngleRef.current;
        const needed = targetAngle - baseAngles[step.idx];
        const delta = shortestDelta(from, needed);
        const next = from + delta;
        applyOrbitRotation(next);
        currentAngleRef.current = next;
      }

      setActiveIndex(step.idx);
      setExpanded(step.expanded);
      setCyclePos(nextPos);
      activeIndexRef.current = step.idx;
      cyclePosRef.current = nextPos;

      window.clearTimeout(timersRef.current.in);
      timersRef.current.in = window.setTimeout(() => {
        setPanelAnimating(false);
      }, panelAppearDelay);

      startAutoplay();
    }, 80);
  };

  // Autoplay: avanza allo step successivo del ciclo dopo l'hold dello step corrente
  const startAutoplay = () => {
    if (reduced) return;
    window.clearTimeout(autoplayRef.current);
    const step = CYCLE[cyclePosRef.current];
    autoplayRef.current = window.setTimeout(() => {
      goToStep((cyclePosRef.current + 1) % CYCLE.length);
    }, step.hold);
  };
  const stopAutoplay = () => {
    window.clearTimeout(autoplayRef.current);
    autoplayRef.current = null;
  };

  // Init / cleanup: allinea l'anello allo step neutro (Vision) senza animazione
  useEffect(() => {
    const node = orbitRef.current;
    const initialIdx = CYCLE[0].idx;
    const initialAngle = targetAngle - baseAngles[initialIdx];

    if (node) {
      node.style.transitionDuration = "0ms";
      node.style.transform = `rotate(${initialAngle}deg)`;
      rootRef.current?.style.setProperty("--orbit-angle", `${initialAngle}deg`);
    }
    currentAngleRef.current = initialAngle;

    setActiveIndex(initialIdx);
    setExpanded(CYCLE[0].expanded);
    setCyclePos(0);
    activeIndexRef.current = initialIdx;
    cyclePosRef.current = 0;

    if (!reduced) startAutoplay();

    return () => {
      stopAutoplay();
      window.clearTimeout(timersRef.current.out);
      window.clearTimeout(timersRef.current.in);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  // Handlers
  const handleDotClick = (pos) => {
    stopAutoplay();
    goToStep(pos);
  };
  const stepForSlide = (slideIdx) =>
    CYCLE.findIndex((s) => s.expanded && s.idx === slideIdx);
  const handleLottieClick = (slideIdx) => {
    const pos = stepForSlide(slideIdx);
    if (pos === -1) return;
    stopAutoplay();
    goToStep(pos);
  };
  // "Scopri di più": salta subito al primo caleidoscopio ingrandito (Purpose)
  const revealCaleidoscopi = () => handleDotClick(1);

  const scrollToSezione = () => {
    const elemento = document.getElementById('sezione-lettere');
    if (elemento) {
      const offsetTop = elemento.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div
      ref={rootRef}
      id="hero"
      className="relative h-[100vh] w-full overflow-hidden bg-blue-300"
      style={{ "--orbit-angle": "0deg" }}
      onMouseEnter={stopAutoplay}
      onMouseLeave={startAutoplay}
    >

      {/* Sfondo animato (Lottie) */}
      <Lottie
        lottieRef={bgLottieRef}
        animationData={bgAnimation}
        loop
        autoplay={!reduced}
        rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ width: "100%", height: "100%" }}
      />

      <div className="flex flex-col md:flex-row h-[100vh] z-10 relative items-center md:pl-5 xl:pl-[calc((100vw-1420px)/2)]">

        {/* Dots di controllo: uno per ciascuno dei 4 step del ciclo */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3 z-30">
          {CYCLE.map((step, i) => (
            <button
              key={i}
              onClick={() => handleDotClick(i)}
              aria-label={`Vai a ${step.expanded ? slides[step.idx].title : "Vision"}`}
              className={`w-4 h-4 rounded-full border-2 transition-all duration-300 hover:scale-110 ${
                cyclePos === i
                  ? "bg-black border-black shadow-lg"
                  : "bg-transparent border-white/60 hover:border-white"
              }`}
              style={{
                backgroundColor: cyclePos === i ? "#000000" : "transparent",
              }}
            />
          ))}
        </div>

        <div className="hidden md:block absolute bottom-[65px] md:left-[50%] translate-x-[-50%] cursor-pointer z-50"
          onClick={scrollToSezione}
        >
          <Lottie
            animationData={ScrollDown}
            loop
            autoplay
          />
        </div>

        {/* Colonna sinistra */}
        <div className="flex flex-col gap-5 justify-end md:justify-center md:items-start w-[100vw] md:w-1/3 h-[64%] md:h-[100%] shrink-0 md:shrink p-5 md:p-0">
          <h1 className="text-white text-[40px] md:text-[50px] lg:text-[60px] leading-[1.1em]">
            Bilancio
            <br />
            Consolidato
            <br />
            Integrato 2025
          </h1>
          <div className="flex flex-col gap-3">
            <a href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf" target="_blank">
              <button className="bt-primary">
                <img
                  src={arrowIcon}
                  alt="Arrow"
                  className="w-[20px] md:w-[40px] h-[20px] md:h-[40px]"
                />
                Scarica il report completo
              </button>
            </a>
            <a href="https://www.realegroup.eu/groupshare/0/IT/Documents/Sostenibilit%C3%A0/2025/EXECUTIVE%20SUMMARY%202025%20(2).pdf" target="_blank">
            <button type="button" className="bt-primary" style={{ width: isMobile ? "60%" : undefined }} title="Link in arrivo">
              <img
                src={arrowIcon}
                alt="Arrow"
                className="w-[20px] md:w-[40px] h-[20px] md:h-[40px]"
              />
              Scarica l&rsquo;Executive Summary
            </button>
            </a>
          </div>
          <div className="flex gap-2">
            <h3 className="text-[18px] md:text-[24px] text-white font-bold">ITA</h3>
            <h3 className="text-[18px] md:text-[24px] text-white font-regular">|</h3>
            <h3 className="text-[18px] md:text-[24px] text-white font-regular"><a href="/en/">ENG</a></h3>
          </div>
        </div>

        {/* Colonna destra */}
        <div className="flex w-[100vw] md:w-2/3 justify-end relative h-[50%] md:h-[100%]">
          {/* Orbita: asse di rotazione stabile con left + translate */}
          <div
            className="absolute"
            style={{
              width: isMobile ? 50 : 500,
              height: isMobile ? 50 : 500,
              left: isMobile ? "35%" : "40%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Lottie "fiori" al centro dell'orbita (non ruota) */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
              style={{ width: isMobile ? 110 : 360, height: isMobile ? 110 : 360 }}
            >
              <Lottie
                animationData={fioriCenter}
                loop
                autoplay
                style={{ width: "100%", height: "100%" }}
              />
            </div>

            {/* Anello che ruota — z-index sopra i fiori centrali (z-10),
                sotto il box di testo (z-20) */}
            <div
              ref={orbitRef}
              className="absolute inset-0"
              style={{ transform: "rotate(0deg)", willChange: "transform", zIndex: 15 }}
            >
              {/* Items posizionati sulla circonferenza; contenuto con contro-rotazione */}
              {slides.map((slide, idx) => {
                const a = baseAngles[idx];
                const isActive = idx === activeIndex;
                const isEnlarged = isActive && expanded;
                const size = isMobile ? 100 : (isMedium ? 200 : 250);

                return (
                  <div
                    key={idx}
                    className="absolute"
                    style={{
                      left: "50%",
                      top: "50%",
                      width: size,
                      height: size,
                      transform: `translate(-50%, -50%) rotate(${a}deg) translate(${radius}px)`,
                      transformOrigin: "50% 50%",
                      zIndex: isActive ? 20 : 5,
                    }}
                  >
                    {/* Contenuto che resta dritto: contro-rotazione -(orbita + a).
                        Nota: SOLO la rotazione sta qui. Lo scale "attivo" è applicato
                        al wrapper dell'icona (sotto), non a questo div, altrimenti la
                        label (posizionata in absolute rispetto a questo box) verrebbe
                        amplificata insieme allo scale e finirebbe fuori dall'area visibile. */}
                    <div
                      className="w-full h-full cursor-pointer"
                      onClick={() => handleLottieClick(idx)}
                      style={{
                        transform: `rotate(calc(-1 * (var(--orbit-angle) + ${a}deg)))`,
                        transition: "transform 300ms linear",
                        transformOrigin: "50% 50%",
                        willChange: "transform",
                      }}
                    >
                      <div
                        className="w-full h-full transition-transform duration-300 hover:scale-110"
                        style={{
                          transform: `scale(${isEnlarged ? 1.8 : 1})`,
                          transformOrigin: "50% 50%",
                        }}
                      >
                        <Lottie
                          animationData={slide.animation}
                          loop
                          autoplay
                          rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
                          style={{
                            width: "100%",
                            height: "100%",
                            opacity: isEnlarged ? 1 : 0.85,
                            filter:
                              !isMobile
                                ? isEnlarged
                                  ? "drop-shadow(0 0 20px rgba(244,114,182,0.45))"
                                  : "none"
                                : "none",
                          }}
                        />
                      </div>

                      {/* Label dritta grazie alla contro-rotazione, e non più dentro il
                          div che si scala quando attivo: resta sempre alla stessa distanza
                          dall'icona invece di essere spinta fuori dallo schermo.
                          Sempre visibile, per non farla sparire durante l'uso del carosello.
                          Posizionata a sinistra (invece che sotto) per restare sempre sullo
                          sfondo blu pieno e garantire il contrasto richiesto dall'accessibilità. */}
                      <div
                        className="hidden absolute top-1/2 right-full -translate-y-1/2 text-white text-sm md:flex md:items-center md:justify-end md:gap-2"
                        style={{
                          transition: "opacity 200ms ease, padding-right 300ms linear",
                          paddingRight: isEnlarged ? "26px" : "16px",
                        }}
                      >
                        <p className="text-2xl text-white whitespace-nowrap leading-none m-0">
                          {slide.title}
                        </p>
                        <img src={arrowIcon} alt="" className="w-[26px] h-[26px] flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Box contenuto */}
          <div
            className="
                absolute right-0 md:right-[-20px] bottom-[-100px] md:bottom-auto md:top-1/2 md:-translate-y-1/2 z-20 h-fit
                w-[150px] md:w-[350px] bg-gradient-to-br from-[#1e2a44dd] to-[#2a3a5add] backdrop-blur-sm p-8 rounded-tl-[40px]
                rounded-bl-[40px] rounded-tr-none rounded-br-none text-white shadow-2xl transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateY(-50%) translateX(${panelAnimating ? "100%" : "0"})`,
            }}
          >
            <div className="min-h-[50px] md:min-h-[120px]">
              {!expanded ? (
                /* Blocco introduttivo: non legato a un caleidoscopio, ricorre a ogni giro del ciclo */
                <div className="flex flex-col items-start">
                  <h2 className="text-base md:text-4xl font-bold mb-3 text-white leading-[1.1em]">
                    Un&rsquo;identità
                    <br />
                    in movimento
                  </h2>
                  <p
                    style={{ lineHeight: "1.15em" }}
                    className="text-gray-200 text-sm md:text-lg lg:text-3xl mb-5"
                  >
                    I caleidoscopi sono il simbolo del nostro lavoro di squadra:
                    la rotazione dei frammenti rappresenta la riconfigurazione di
                    una realtà sempre più complessa e più bella della precedente.
                  </p>
                  <a href="https://www.realegroup.eu/groupshare/0/IT/ListaReportsAndAccounts/87/BILANCIO%20CONSOLIDATO%20RG%202025.pdf#page=2" target="_blank">
                  <button
                    type="button"
                    onClick={revealCaleidoscopi}
                    className="flex items-center gap-2 bg-white text-[#004996] font-bold rounded-full p-[10px] md:px-5 md:py-2 text-[12px] md:text-base hover:bg-gray-100 transition-colors"
                  >
                    <img src={arrowIcon} alt="" className="hidden md:block w-[20px] h-[20px]" />
                    Scopri di più
                  </button>
                  </a>
                </div>
              ) : (
                <>
                  <div className="flex flex-col items-start mb-4 ">
                    <div className="mb-2 w-[40px] h-[40px] md:w-[80px] md:h-[80px]">
                      <Lottie animationData={slides[activeIndex].icon} loop autoplay />
                    </div>
                    <h2
                      className={`text-base md:text-4xl font-bold flex items-center mb-2 ${
                        slides[activeIndex].title === "Vision" ? "mt-4" : ""
                      }`}
                      style={{ color: slides[activeIndex].color }}
                    >
                      {slides[activeIndex].title}
                    </h2>
                  </div>
                  <p
                    style={{ lineHeight: "1.1em" }}
                    className="text-gray-200 leading-relaxed text-base md:text-xl lg:text-3xl "
                  >
                    {slides[activeIndex].text}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
